import {AGENCY_KEYS} from "@/api/agencyApi";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import CompanyLogoPlaceholder from "@/assets/images/company_logo_placeholder.svg";
import CampaignAccordion from "@/components/Campaign/common/CampaignAccordion";
import Button from "@/components/Common/Button";
import Loader from "@/components/Common/kpi/Loader";
import Modal from "@/components/Common/Modal";
import type {CompanyProfileMergePreviewSerializer, PackageSubscriptionSerializer} from "@/generated/DjangoTypes";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import CombineAccountsForm from "@/pages/agencies/CombineAccountsForm";
import CompanyListDropdown from "@/pages/agencies/CompanyListDropdown";
import useCombineBrandsStore from "@/store/combineBrands";
import {INFINITY_THRESHOLD} from "@/store/constants";
import type {TCurrency} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {createFileRoute, useRouter} from "@tanstack/react-router";
import {Fragment, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";
import {useShallow} from "zustand/shallow";

type TMergeBrandsPreview = {
  buyout_used: number;
  company_profiles: CompanyProfileMergePreviewSerializer[];
  package: PackageSubscriptionSerializer | null;
  total_credits: number;
};

export const Route = createFileRoute("/_authenticated/_admin/admins/accounts/combine")({
  component: CombineAccounts,
});

function CombineAccounts() {
  const {t} = useTranslation(["multi_account", "common"]);

  useTitle(t("combine_brands_page_title"));

  const navigate = Route.useNavigate();
  const router = useRouter();
  const form = useCombineBrandsStore(state => state.form);
  const isSingleBrandUpgrade = useCombineBrandsStore(state => state.isSingleBrandUpgrade);
  const isTrialing = useCombineBrandsStore(state => state.isTrialing());
  const largeBrand = useCombineBrandsStore(state => state.largeBrand);
  const reset = useCombineBrandsStore(state => state.reset);
  const selectedCompanyIds = useCombineBrandsStore(useShallow(state => state.getSelectedCompanyIds()));
  const selectedDropdownItems = useCombineBrandsStore(state => state.selectedDropdownItems);
  const setter = useCombineBrandsStore(state => state.setter);
  const showForm = isSingleBrandUpgrade ? selectedCompanyIds.length === 1 : selectedCompanyIds.length >= 2;
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [resetModalVariant, setResetModalVariant] = useState<"FORM_RESET" | "NAVIGATION_RESET" | null>(null);

  const {data: preview, isFetching: isFetchingPreview} = useGenericQuery<TMergeBrandsPreview>({
    queryKey: AGENCY_KEYS.qAgencyMergeBrandsPreview(selectedCompanyIds),
    enabled: showForm,
  });
  const {mutate: mergeBrands, isPending: isMergingBrands} = useMutation({
    mutationKey: AGENCY_KEYS.mAgencyBrandsMerge,
    mutationFn: () =>
      client.post(AGENCY_KEYS.mAgencyBrandsMerge[0], {
        cash_reward_service_fee_percentage: form.influencer_fees.regular,
        company_profile_ids: selectedCompanyIds.join(","),
        credits: form.credits,
        large_brand: largeBrand,
        owner_email: form.owner_email,
        number_of_campaigns: form.number_of_campaigns,
        number_of_seats: form.number_of_seats,
        invitations_per_campaign: form.invitations_per_campaign,
        influencers_per_campaign: form.influencers_per_campaign,
        buyout: form.buyouts_per_year,
        extra_campaign_price: form.extra_campaign_price,
        extra_seat_price: form.extra_seat_price,
        sales_email: form.sales_email,
        relationship_manager_email: form.relationship_manager_email,
        valid_from: form.valid_from,
        valid_until: form.valid_until,
        instagram_campaigns: !!form.instagram_campaigns,
        tiktok_campaigns: !!form.tiktok_campaigns,
        package_type: form.package_type,
        price_per_month: form.price_per_month,
        price_per_year: form.price_per_year,
        pro_service_fee_percentage: form.influencer_fees.pro,
        currency: form.currency,
      }),
    onSuccess: () => {
      reset();
      setIsConfirmationModalOpen(false);
      navigate({to: "/admins/customers"});
    },
  });

  useEffect(() => {
    const buyoutsUsed = preview?.buyout_used;
    const pkg = preview?.package;
    const firstCompany = selectedDropdownItems[0].company;

    if (pkg) {
      setter(state => {
        state.form.buyouts_per_year =
          typeof pkg.buyout === "number" && pkg.buyout < INFINITY_THRESHOLD ? pkg.buyout : null;
        state.form.buyouts_used = buyoutsUsed;
        state.form.credits = preview.total_credits;
        state.form.extra_campaign_price = pkg.extra_campaign_price;
        state.form.extra_seat_price = pkg.extra_seat_price;
        state.form.influencer_fees = {
          pro: firstCompany?.agency.pro_service_fee_percentage,
          regular: firstCompany?.agency.cash_reward_service_fee_percentage,
        };
        state.form.influencers_per_campaign =
          typeof pkg.influencers_per_campaign === "number" && pkg.influencers_per_campaign < INFINITY_THRESHOLD
            ? pkg.influencers_per_campaign
            : null;
        state.form.invitations_per_campaign =
          typeof pkg.invitations_per_campaign === "number" && pkg.invitations_per_campaign < INFINITY_THRESHOLD
            ? pkg.invitations_per_campaign
            : null;
        state.form.number_of_campaigns =
          typeof pkg.number_of_campaigns === "number" && pkg.number_of_campaigns < INFINITY_THRESHOLD
            ? pkg.number_of_campaigns
            : null;
        state.form.number_of_seats =
          typeof pkg.number_of_seats === "number" && pkg.number_of_seats < INFINITY_THRESHOLD
            ? pkg.number_of_seats
            : null;
        state.form.valid_from = pkg.valid_from;
        state.form.valid_until = pkg.valid_until;
        state.form.instagram_campaigns = pkg.instagram_campaigns;
        state.form.tiktok_campaigns = pkg.tiktok_campaigns;
        state.form.price_per_month = pkg.price_per_month;
        state.form.price_per_year = pkg.price_per_year;
        state.form.package_type = pkg.package_type!;
        state.form.currency = pkg.currency as TCurrency;
      });
    }
  }, [preview, setter]);
  useEffect(() => {
    const initialOwnerEmail = selectedDropdownItems[0]?.company?.owner_email;

    if (initialOwnerEmail) {
      setter(state => {
        state.form.owner_email = initialOwnerEmail;
      });
    }
  }, [setter, selectedDropdownItems[0]?.company?.owner_email]);

  return (
    <>
      {isConfirmationModalOpen && (
        <Modal
          backgroundClassName="max-w-200"
          customTitleRow={
            <div className="text-neutral-black flex items-center gap-2 p-5 text-xl font-semibold">
              <WarningIcon className="text-warning-medium-yellow size-5 shrink-0" />
              {t(
                isSingleBrandUpgrade
                  ? "combine_brands_confirmation_modal_title_simple_upgrade"
                  : "combine_brands_confirmation_modal_title",
              )}
            </div>
          }
          customBottomRow={
            <div className="border-neutral-white-dark flex shrink-0 grow gap-3 border-t p-5">
              <Button
                onClick={() => mergeBrands()}
                buttonStyle="Gradient"
                isLoading={isMergingBrands}
                className="h-auto w-full px-8 py-4">
                {t(isSingleBrandUpgrade ? "confirm_upgrade" : "confirm_combination")}
              </Button>
            </div>
          }
          onClose={() => setIsConfirmationModalOpen(false)}>
          <div className="text-neutral-blac mb-5 flex flex-col gap-10">
            <div className="flex flex-col gap-5">
              <span className="text-sm">
                {t(
                  isSingleBrandUpgrade
                    ? "combine_brands_confirmation_modal_body_simple_upgrade"
                    : "combine_brands_confirmation_modal_body",
                )}
              </span>
              {isTrialing && <span className="text-sm">{t("package_inactive_trial")}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {selectedDropdownItems
                .filter(item => !!item.company)
                .map((item, idx, arr) => {
                  const company = item.company;

                  return (
                    <Fragment key={item.id}>
                      <div className="flex items-center gap-2">
                        <img
                          src={company?.picture320 ?? company?.picture ?? CompanyLogoPlaceholder}
                          className="border-primary-light-solid size-7 shrink-0 rounded-full border"
                        />
                        <span>{company?.company_name}</span>
                      </div>
                      {idx < arr.length - 1 && <span className="text-neutral-grey-dark text-sm font-medium">/</span>}
                    </Fragment>
                  );
                })}
            </div>
            <CombineAccountsForm variant="SHORT" />
          </div>
        </Modal>
      )}
      {!!resetModalVariant && (
        <Modal
          backgroundClassName="max-w-150"
          modalTitle={t("combine_brands_reset_warning_modal_title")}
          onClose={() => setResetModalVariant(null)}
          onConfirm={() => {
            reset();

            if (resetModalVariant === "NAVIGATION_RESET") {
              router.history.back();
            }
          }}>
          <div className="bg-warning-light-yellow text-warning-dark-yellow rounded-md p-4 text-lg font-semibold">
            {t("combine_brands_reset_warning_modal_body")}
          </div>
        </Modal>
      )}
      <div className="max-w-wide container mx-auto mt-8 mb-60 flex flex-col gap-6 px-8 lg:gap-10">
        <button
          onClick={() => setResetModalVariant("NAVIGATION_RESET")}
          className="text-neutral-black flex w-fit items-center gap-2">
          <RightChevronIcon className="shrink-0 rotate-180" />
          <span className="text-lg font-semibold">{t("cancel")}</span>
        </button>
        <h3 className="text-neutral-black my-0 font-semibold">{t("combine_brands_page_title")}</h3>
        <div className="border-neutral-grey-light-dark bg-neutral-grey-lightest flex items-center gap-3 rounded-sm border p-3">
          <WarningIcon className="text-danger-medium-red size-3.5 shrink-0" />
          <span className="text-neutral-grey-dark text-xs">{t("combine_brands_warning")}</span>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-neutral-black text-sm font-semibold">{t("choose_multi_account_type")}</span>
          {(["agency", "large_brand"] as const).map(type => (
            <button
              key={type}
              onClick={() =>
                setter(state => {
                  state.largeBrand = type === "large_brand";
                })
              }
              className={clsxm(
                "relative flex w-full items-center justify-between rounded-sm border p-4 text-lg transition-all",
                largeBrand === (type === "large_brand")
                  ? "border-primary-medium-solid text-primary-medium-solid font-semibold"
                  : "border-primary-light-solid",
              )}>
              <span className="z-10 mr-2 text-left">{t(`${type}_with_example`)}</span>
              <div
                className={clsxm(
                  "absolute inset-0 z-0 rounded-sm transition-all",
                  largeBrand === (type === "large_brand") ? "bg-primary-light-solid opacity-50" : "bg-transparent",
                )}
              />
              <span
                className={clsxm(
                  "z-10 flex size-5 items-center justify-center rounded-full border",
                  largeBrand === (type === "large_brand")
                    ? "border-primary-medium-solid"
                    : "border-primary-light-solid",
                )}>
                <span
                  className={clsxm(
                    "size-3.5 rounded-full transition-all",
                    largeBrand === (type === "large_brand") ? "bg-primary-dark-solid" : "bg-transparent",
                  )}
                />
              </span>
            </button>
          ))}
        </div>
        {selectedDropdownItems.map(item => (
          <CompanyListDropdown key={item.id} selectedItem={item} isDisabled={largeBrand === null} />
        ))}
        {showForm && (
          <CampaignAccordion
            isSticky
            title={t("new_multiaccount_limits_and_setup")}
            containerClassName="border border-neutral-grey-lightest rounded-lg bg-white">
            {isFetchingPreview ? <Loader /> : <CombineAccountsForm />}
          </CampaignAccordion>
        )}
        <div className="flex flex-wrap items-center justify-end gap-5">
          <Button
            onClick={() => setResetModalVariant("FORM_RESET")}
            isDisabled={largeBrand === null}
            buttonStyle="White"
            className="h-auto px-16 py-4">
            {t("cancel")}
          </Button>
          <Button
            onClick={() => setIsConfirmationModalOpen(true)}
            isDisabled={selectedCompanyIds.length < (isSingleBrandUpgrade ? 1 : 2) || !form.owner_email}
            buttonStyle="Gradient"
            className="h-auto px-16 py-4">
            {t(isSingleBrandUpgrade ? "upgrade_brand" : "combine_brands")}
          </Button>
        </div>
      </div>
    </>
  );
}
